/* eslint-disable @typescript-eslint/no-explicit-any */

import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';

import { Job, JobTagFormat } from '@/services/public-jobs.service';

export const transformPublicJob = (job: any): Job => {
  return {
    id: job.id,
    code: job.code,
    discipline: job.discipline,
    disciplineTitle: transformJobDisciplineTitle(job.discipline),
    title: job.title,
    simpleTitle: transformSimpleTitle(job),
    shift: transformShiftType(job),
    tags: transformTags(job),
    startDate: transformStartDate(job),
    contractWeeks: job.contract_length,
    hiredNomads: job.tags.social_proof,
    facility: job.source,
    coverImage: job.facility_photo,
    weeklyPay: transformWeeklyPay(job),
    location: transformJobLocation(job),
    specialty: transformJobSpecialty(job),
  };
};

const transformShiftType = (job: any): string => {
  if (!job.shift_types.length) {
    return job.shift_hours_and_days;
  }

  return (
    job.shift_types
      .map((shiftType: any) => shiftType[0].toUpperCase() + shiftType.slice(1))
      .join(', ') +
    ', ' +
    job.shift_hours_and_days
  );
};

// Check https://github.com/NomadHealth/nomad-web-app/blob/master/contexts/jobs-store/types.ts#L17
const transformTags = (job: any): JobTagFormat[] => {
  const tags: JobTagFormat[] = [];

  if (job.tags.above_average) {
    tags.push('aboveAvgPay');
  }
  if (job.tags.already_applied) {
    tags.push('applicationSubmitted');
  }
  if (job.tags.auto_offer) {
    tags.push('autoOffer');
  }
  if (job.tags.featured) {
    tags.push('featured');
  }
  if (job.tags.first_to_apply) {
    tags.push('firstToApply');
  }
  if (job.tags.hot) {
    tags.push('topMatch');
  }
  if (job.tags.last_modified === 'new') {
    tags.push('new');
  }
  if (job.tags.nomad_exclusive) {
    tags.push('exclusive');
  }
  if (job.tags.social_proof) {
    tags.push(['nomadsWorkedHere', Number(job.tags.social_proof)]);
  }

  return tags;
};

const transformStartDate = (job: any): string => {
  if (job.start_date.toLowerCase() === 'immediate') {
    return 'Immediate';
  }

  return new Date(job.start_date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
};

const transformWeeklyPay = (job: any): number =>
  parseFloat(job.weekly_gross_compensation.replace(/[^0-9.]/g, ''));

const transformSimpleTitle = (job: any): string => {
  return job.specialization_names?.[0]?.name || job.title;
};

const transformJobLocation = (job: any): Job['location'] => ({
  city: job.facility_city,
  state: job.facility_state,
});

const transformJobSpecialty = (job: any): string => {
  return job.specialization_names?.length
    ? job.specialization_names[0].name
    : '';
};

export const transformJobDisciplineTitle = (discipline: string) => {
  return startCase(camelCase(discipline));
};
